/**
 * Labiarge language control, progressive enhancement.
 *
 * Horizon renders the language switcher as a native <select> (desktop header
 * and mobile menu drawer, both from snippets/localization-form.liquid). This
 * module replaces it with a brand-styled trigger that opens the Labiarge
 * localization modal. One surface for both language and currency, on desktop
 * and as a bottom sheet on mobile. Instead of an OS combo popup.
 *
 * Contract:
 *   - Progressive: the trigger is only built when the modal template is present.
 *     If either this module or the modal never loads, the native <select> stays
 *     visible and fully functional.
 *   - Owns no state. Clicking dispatches `labiarge:localization:open`;
 *     labiarge-localization-modal.js does the rest, including the actual
 *     language change (it submits the same vendor localization form).
 *
 * Header currency: `show_country` in sections/header-group.json stays **false**
 * on purpose. With it true, Horizon renders its own combined country+language
 * popover with a trigger this module does not own, and there would be two
 * competing currency UIs. Currency belongs to the modal. It lights up there by
 * itself as soon as the store presents more than one currency.
 */

const TEMPLATE_ID = 'labiarge-localization-modal-template';
const OPEN_EVENT = 'labiarge:localization:open';
const READY_CLASS = 'labiarge-lang-select--ready';
const ENHANCED_ATTR = 'data-labiarge-enhanced';

const CARET_SVG =
  '<svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true" focusable="false">' +
  '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Desktop header row and mobile menu drawer both render a standalone select. */
const SELECT_SELECTOR = [
  '.dropdown-localization select.localization-form__select',
  '.menu-drawer__localization select.localization-form__select',
].join(',');

/**
 * @param {ParentNode} [root]
 */
function enhanceAll(root = document) {
  // No modal, no replacement. The native control must keep working.
  if (!document.getElementById(TEMPLATE_ID)) return;

  for (const select of root.querySelectorAll(SELECT_SELECTOR)) {
    if (!(select instanceof HTMLSelectElement)) continue;
    // Skip the language <select> nested inside Horizon's country popover, that
    // trigger belongs to the vendor component, not to us.
    if (select.closest('.localization-wrapper')) continue;
    enhance(select);
  }
}

/**
 * @param {HTMLSelectElement} select
 */
function enhance(select) {
  if (select.getAttribute(ENHANCED_ATTR) === 'true') return;

  const options = Array.from(select.options);
  if (options.length < 2) return; // nothing to pick. Leave the native control

  const container = select.closest('.language-selector') || select.parentElement;
  if (!(container instanceof HTMLElement)) return;

  select.setAttribute(ENHANCED_ATTR, 'true');

  const label = select.getAttribute('aria-label') || 'Language';
  const selectedOption = options[select.selectedIndex] || options[0];

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'labiarge-lang-select__trigger';
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-label', label);

  const valueEl = document.createElement('span');
  valueEl.className = 'labiarge-lang-select__value';
  valueEl.textContent = (selectedOption?.textContent || '').trim();

  const caret = document.createElement('span');
  caret.className = 'labiarge-lang-select__caret';
  caret.setAttribute('aria-hidden', 'true');
  caret.innerHTML = CARET_SVG;

  trigger.append(valueEl, caret);
  trigger.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent(OPEN_EVENT));
  });

  container.append(trigger);
  container.classList.add('labiarge-lang-select', READY_CLASS);
}

// Module scripts are deferred. The DOM is parsed by the time this runs.
enhanceAll();

// Theme editor re-renders the header section without a full reload.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  enhanceAll(target instanceof HTMLElement ? target : document);
});
